import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import ConsensusChart from "../../components/ConsensusChart";
import SearchInput from "../../components/SearchInput";

import TableContent from "../TableContent";

export default function Consensus() {
  const [isLoading, setIsLoading] = useState(false);
  const [chartInfo, setChartInfo] = useState(null);
  const [data, setData] = useState([]);
  const [companylist, setCompanylist] = useState([]);
  const [{ today, last }, setDate] = useState({});

  const [top3Revenue, setTop3Revenue] = useState([]);
  const [top3OP, setTop3OP] = useState([]);

  useEffect(() => {
    (async () => {
      const fetched = await axios.get("/api/company");

      setCompanylist(fetched.data);
    })();

    (async () => {
      setIsLoading(true);
      const today = new Date();
      const year = today.getFullYear().toString();

      let temp_data = [];

      let today_days = 0;
      let last_days = 1;
      while (true) {
        try {
          // ... (existing fetch logic) ...
          const { data: res_today } = await axios.get(
            `/api/consensus/dateinterval/${today_days}/year/${year + "12"}`
          );
          const { data: res_last } = await axios.get(
            `/api/consensus/dateinterval/${last_days}/year/${year + "12"}`
          );

          if (!res_today || !res_last || res_today.length === 0) {
             today_days += 1;
             last_days += 1;
             if (today_days > 60) break;
             continue;
          }

          setDate({
            today: res_today[0].date.slice(5),
            last: res_last[0].date.slice(5),
          });

          const getGrowth = (curr, prev) => {
             // ... (existing logic) ...
            if (curr >= 0) {
              if (prev >= 0) {
                return (curr / prev - 1) * 100;
              } else {
                return "흑자전환";
              }
            } else {
              if (prev >= 0) {
                return "적자전환";
              } else {
                if (curr > prev) {
                  return "적자축소";
                } else {
                  return "적자확대";
                }
              }
            }
          };

          res_today.forEach((today_item) => {
            res_last.forEach((last_item) => {
              if (
                today_item.company_id === last_item.company_id &&
                (today_item.revenue !== last_item.revenue ||
                  today_item.operatingProfit !== last_item.operatingProfit)
              ) {
                const temp = {
                  // ... (existing object creation) ...
                  name: today_item.name,
                  target_year: today_item.year,
                  company_id: today_item.company_id,
                  today_revenue: today_item.revenue,
                  last_revenue: last_item.revenue,
                  revenue_change: getGrowth(
                    today_item.revenue,
                    last_item.revenue
                  ),
                  today_operatingProfit: today_item.operatingProfit,
                  last_operatingProfit: last_item.operatingProfit,
                  operationProfit_change: getGrowth(
                    today_item.operatingProfit,
                    last_item.operatingProfit
                  ),
                  code: today_item.code,
                  date: today_item.date,
                };

                temp_data.push(temp);
              }
            });
          });

          if (temp_data.length !== 0) {
            break;
          } else {
            today_days += 1;
            last_days += 1;
          }
        } catch (err) {
          console.error(err);
          break;
        }
      }

      // --- Summary Logic ---
      const sortedByRev = [...temp_data]
        .filter(d => typeof d.revenue_change === 'number')
        .sort((a, b) => b.revenue_change - a.revenue_change)
        .slice(0, 3);
      
      const sortedByOP = [...temp_data]
        .filter(d => typeof d.operationProfit_change === 'number')
        .sort((a, b) => b.operationProfit_change - a.operationProfit_change)
        .slice(0, 3);

      setTop3Revenue(sortedByRev);
      setTop3OP(sortedByOP);

      setData(temp_data);
      setIsLoading(false);
    })();
  }, []);

  const handleChartInfo = ({ company_id, name }) => {
    setChartInfo({ company_id, name });
  };
  
  const defaultNumberSort = (rowA, rowB, columnId) => {
    const valA = rowA.values[columnId];
    const valB = rowB.values[columnId];

    if (!isNaN(valA)) {
      if (isNaN(valB)) {
        return 1;
      }
      return Number(valA) >= Number(valB) ? 1 : -1;
    } else {
      switch (valA) {
        case "흑자전환":
          return 1;
        case "적자전환":
          return -1;
        case "적자확대":
          return -1;
        case "적자축소":
          if (valB === "적자확대" || valB === "적자전환") {
            return 1;
          } else {
            return -1;
          }
        default:
          return -1;
      }
    }
  };

  const columns = useMemo(
    () => [
      // ... (existing columns) ...
      {
        Header: "회사명",
        accessor: "name",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ value }) => <span className="font-bold text-gray-800">{value}</span>
      },
      {
        Header: `매출(${today})`,
        accessor: "today_revenue",
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ value }) => {
          return <span className="tabular-nums tracking-tight">{value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>;
        },
      },
      {
        Header: `매출(${last})`,
        accessor: "last_revenue",
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ value }) => {
          return <span className="tabular-nums tracking-tight text-gray-400">{value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>;
        },
      },
      {
        Header: "매출변동",
        accessor: "revenue_change",
        disableFilters: true,
        Cell: ({ value }) => {
          if (typeof value !== 'number') {
            return (
               <span className="px-2 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-600">
                 {value}
               </span>
            );
          }
          const isPlus = value > 0;
          return (
            <span
              className={`px-2 py-1 rounded-md text-xs font-bold tabular-nums ${
                isPlus ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"
              }`}
            >
              {isPlus ? "+" : ""}{value.toFixed(1)}%
            </span>
          );
        },
        type: "change",
        sortType: defaultNumberSort,
      },
      {
        Header: `OP(${today})`,
        accessor: "today_operatingProfit",
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ value }) => {
           return <span className="tabular-nums tracking-tight">{value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>;
        },
      },
      {
        Header: `OP(${last})`,
        accessor: "last_operatingProfit",
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ value }) => {
           return <span className="tabular-nums tracking-tight text-gray-400">{value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>;
        },
      },
      {
        Header: "OP변동",
        accessor: "operationProfit_change",
        disableFilters: true,
        Cell: ({ value }) => {
          if (typeof value !== 'number') {
             return (
               <span className="px-2 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-600">
                 {value}
               </span>
            );
          }
          const isPlus = value > 0;
          return (
            <span
              className={`px-2 py-1 rounded-md text-xs font-bold tabular-nums ${
                isPlus ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"
              }`}
            >
              {isPlus ? "+" : ""}{value.toFixed(1)}%
            </span>
          );
        },
        type: "change",
        sortType: defaultNumberSort,
      },
    ],
    [today, last]
  );
  
  // Summary Card Component
  const SummaryCard = ({ title, items, color }) => (
    <div className="flex-1 bg-white rounded-2xl p-4 border border-gray-100 shadow-sm min-w-[280px]">
      <div className="flex items-center gap-2 mb-3">
         <div className={`w-1 h-4 rounded-full ${color === 'red' ? 'bg-rose-500' : 'bg-indigo-500'}`}></div>
         <h3 className="text-sm font-bold text-gray-700">{title}</h3>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex justify-between items-center text-sm group cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
               onClick={() => handleChartInfo({ company_id: item.company_id, name: item.name })}
          >
            <div className="flex items-center gap-2">
               <span className="text-xs font-mono text-gray-400 w-4">{i+1}</span>
               <span className="font-medium text-gray-800 group-hover:text-blue-600 transition-colors">{item.name}</span>
            </div>
            <span className={`font-bold tabular-nums ${color==='red'?'text-rose-500':'text-indigo-500'}`}>
              +{color === 'red' ? item.revenue_change.toFixed(1) : item.operationProfit_change.toFixed(1)}%
            </span>
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-gray-400 py-2">데이터 집계 중...</div>}
      </div>
    </div>
  );

  return (
    <>
      <TableContent
        isLoading={isLoading}
        columns={columns}
        data={data}
        handleChartInfo={handleChartInfo}
      >
        {/* Top Summary Section */}
        {!isLoading && data.length > 0 && (
          <div className="flex flex-col md:flex-row gap-4 mb-6 px-1">
            <SummaryCard title={`매출 급등 Top 3 (${today})`} items={top3Revenue} color="red" />
            <SummaryCard title={`영업이익 급등 Top 3 (${today})`} items={top3OP} color="indigo" />
          </div>
        )}

        {/* Glass Toolbar */}
        <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 py-3 px-1 border-b border-gray-100 flex justify-between items-center transition-all mb-2 rounded-xl">
           <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-800">Consensus Filter</span>
              {data && data[0] && (
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {data[0]?.target_year.slice(0, 4)}년
                </span>
              )}
           </div>
           <SearchInput datalist={companylist} handleKeyword={handleChartInfo} />
        </div>

        {chartInfo?.company_id ? (
          <div className="mt-4 mb-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 overflow-hidden">
             <ConsensusChart
               company_id={chartInfo?.company_id}
               name={chartInfo?.name}
             />
          </div>
        ) : null}

      </TableContent>
    </>
  );
}
